import { FC, useMemo } from 'react'

import { ColumnLayout, Container, Header, SpaceBetween, Table } from '@cloudscape-design/components'
import { KeyValuePairs } from '@aws-northstar/ui'

import { ACCOUNT_NETWORK_ALL_FIELDS } from '../../constants/accountNetworkRequestFields'
import {
	accountHasNetworkRequestData,
	accountRecordShowsIpAllocationAzC,
	formatAccountNetworkFieldValue,
	formatIpAllocationsForTable
} from '../../helper/accountRequestNetworkSummary'
import type { IpAddressAllocation } from '../../types/cluster'

type Props = {
	details: Record<string, unknown> | undefined
	accountType: string | undefined
	networkSectionTitle?: string
	networkSectionHeaderVariant?: 'h2' | 'h3'
	ipTableHeaderVariant?: 'h2' | 'h3'
	wrapInContainer?: boolean
}

const COLUMNS = 3

function formatDisplayValue(key: string, details: Record<string, unknown> | undefined): string {
	const raw = formatAccountNetworkFieldValue(key, details?.[key])
	if (raw === '—') return '—'
	return raw.replace(/\n/g, ', ')
}

function buildKeyValueRows(
	details: Record<string, unknown> | undefined
): Array<Array<{ label: string; value: string }>> {
	const cells = ACCOUNT_NETWORK_ALL_FIELDS.filter(({ key }) => {
		if (key !== 'secondaryCidrBlock' && key !== 'publicCidrBlock') return true
		return formatAccountNetworkFieldValue(key, details?.[key]) !== '—'
	}).map(({ key, label }) => ({
		label,
		value: formatDisplayValue(key, details)
	}))

	const rows: Array<Array<{ label: string; value: string }>> = []
	for (let i = 0; i < cells.length; i += COLUMNS) {
		rows.push(cells.slice(i, i + COLUMNS))
	}
	return rows
}

const AccountNetworkReadonlyView: FC<Props> = ({
	details,
	accountType,
	networkSectionTitle = 'Network details',
	networkSectionHeaderVariant = 'h3',
	ipTableHeaderVariant = 'h3',
	wrapInContainer = true
}) => {
	const ipAllocRows = formatIpAllocationsForTable(details?.ip_address_allocations)
	const showIpAllocAzC = accountRecordShowsIpAllocationAzC(accountType)
	const keyValueRows = useMemo(() => buildKeyValueRows(details), [details])

	const ipColumnDefinitions = useMemo(
		() => [
			{
				id: 'label',
				header: 'Label',
				width: 200,
				cell: (item: IpAddressAllocation) => item.label,
				isRowHeader: true
			},
			{
				id: 'a',
				header: 'AZ A',
				minWidth: 168,
				cell: (item: IpAddressAllocation) => item.cidrAzA?.trim() || '—'
			},
			{
				id: 'b',
				header: 'AZ B',
				minWidth: 168,
				cell: (item: IpAddressAllocation) => item.cidrAzB?.trim() || '—'
			},
			...(showIpAllocAzC
				? [
						{
							id: 'c',
							header: 'AZ C',
							minWidth: 168,
							cell: (item: IpAddressAllocation) => item.cidrAzC?.trim() || '—'
						}
				  ]
				: [])
		],
		[showIpAllocAzC]
	)

	if (!accountHasNetworkRequestData(details)) {
		return null
	}

	const inner = (
		<SpaceBetween direction="vertical" size="l">
			<ColumnLayout columns={COLUMNS} variant="text-grid">
				<KeyValuePairs items={keyValueRows} />
			</ColumnLayout>

			{ipAllocRows.length > 0 && (
				<Table
					header={<Header variant={ipTableHeaderVariant}>IP address allocation</Header>}
					variant="embedded"
					columnDefinitions={ipColumnDefinitions}
					items={ipAllocRows}
					stickyColumns={{ first: 1 }}
				/>
			)}
		</SpaceBetween>
	)

	if (!wrapInContainer) {
		return inner
	}

	return (
		<Container
			header={<Header variant={networkSectionHeaderVariant}>{networkSectionTitle}</Header>}
		>
			{inner}
		</Container>
	)
}

export default AccountNetworkReadonlyView
